import { ReactElement, useState } from 'react';

import useCurrentUser from 'hooks/useCurrentUser';
import { closeWebview } from 'utils/webview';
import { Route, FeatureFlag, ApiRoute } from 'config';

import { Component as PCLogo } from 'consumer/components/logos/pclogo-black.svg';
import { Component as MenuIcon } from 'icons/icon-ui--menu.svg';
import { Component as CloseIcon } from 'icons/icon-ui--close.svg';
import { Component as CaretIcon } from 'icons/icon-ui--caret-outline.svg';
import { Component as DiscountIcon } from 'icons/icon-circle--discount.svg';

import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import Link from './Link';
import SubLink from './SubLink';
import UserBadge from './UserBadge';

import useAsyncEffect from '../../../hooks/useAsyncEffect';
import { get } from '../../../utils/network';
import ISku from '../../../models/ISku';
import { removeTrailingDecimals } from '../../../utils/strings';

type Props = {
  isMobileNavActive?: boolean;
  onMobileNavClick?: () => void;
};

function Header({
  isMobileNavActive = false,
  onMobileNavClick = () => null,
}: Props): ReactElement {
  const { currentUser } = useCurrentUser();
  const {
    isEmpty, isInactive, coupon_message: couponMessage,
    coupon_tooltip_message: couponTooltipMessage,
    active_coupon: activeCoupon,
  } = currentUser;

  const hasFrozenMealsEnabled = currentUser.hasFeatureEnabled(
    FeatureFlag.FROZEN_MEALS,
  );
  const isAccountManagementEnabled = currentUser.hasFeatureEnabled(FeatureFlag.ACCOUNT_MANAGEMENT);
  const isBundlesEnabled = currentUser.hasFeatureEnabled(FeatureFlag.BUNDLES);
  const [isBundlesAvailable, setIsBundlesAvailable] = useState(false);

  // TODO: when the Menus dropdown goes away, this can be removed
  useAsyncEffect(async (isMounted) => {
    const { parsedBody, ok } = await get<ISku[]>(encodeURI(
      `${ApiRoute.MENU_PRODUCTS}?section=plantry`,
    ));

    if (isMounted.current && ok) {
      const availableBundles = parsedBody?.filter((product) => product.product_type === 'bundle');
      // @ts-expect-error TODO: fix null safety
      setIsBundlesAvailable(availableBundles?.length > 0);
    }
  }, []);

  return (
    <header className="c-header" role="banner">
      <div className="l-container">
        <a
          title="Purple Carrot"
          className="c-logo-pc"
          data-cy="pc-logo"
          aria-label="Home"
          href="/"
          tabIndex={0}
        >
          <PCLogo width="135" focusable="false" />
        </a>

        {!isEmpty && (
          <button
            type="button"
            className={[
              'c-nav-offcanvas-trigger',
              isMobileNavActive ? 'is-active' : '',
              'd-mapp-none',
            ].join(' ')}
            data-cy="header-offcanvas-trigger"
            title="Toggle site navigation"
            aria-expanded={isMobileNavActive}
            onClick={onMobileNavClick}
          >
            <div className="c-nav-offcanvas-trigger__icon">
              <MenuIcon
                className={[
                  'c-nav-offcanvas-trigger__icon-menu',
                  'c-svg-fill-gray-068-color',
                ].join(' ')}
                width="20"
                focusable="false"
              />
              <CloseIcon
                className={[
                  'c-nav-offcanvas-trigger__icon-close',
                  'c-svg-fill-gray-068-color',
                ].join(' ')}
                width="24"
                focusable="false"
              />
            </div>
          </button>
        )}

        {!isEmpty && (
          <nav
            className="c-nav-header d-mapp-none d-iframe-none"
            aria-label="Site navigation"
          >
            <ul className="c-nav-header__list">
              {isInactive ? (
                <>
                  <Link
                    href={Route.WEEKLY_MENU_MEALS}
                    data-cy="header-view-menu"
                    reload
                  >
                    View Menu
                  </Link>
                  <Link
                    href="https://purplecarrot.zendesk.com/hc/en-us"
                    data-cy="header-faqs"
                    target="_blank"
                    rel="noreferrer"
                    reload
                  >
                    FAQs
                  </Link>
                </>
              ) : (
                <>
                  {activeCoupon && (
                  <li className="c-nav-header__discount">
                    <span>
                      <DiscountIcon />
                    </span>
                    <span>
                      {couponMessage}
                      <OverlayTrigger
                        placement="bottom"
                        overlay={(
                          <Tooltip id="discount-header-tooltip">
                            {removeTrailingDecimals(couponTooltipMessage)}
                          </Tooltip>
                              )}
                      >
                        <button
                          className="c-link c-info-icon c-info-symbol"
                          type="button"
                          tabIndex={0}
                        >
                          &#9432;
                        </button>
                      </OverlayTrigger>
                    </span>
                  </li>
                  )}
                  <Link
                    href={Route.UPCOMING_MENUS}
                    data-cy="header-my-dashboard"
                  >
                    {isAccountManagementEnabled ? 'Upcoming Deliveries' : 'My Dashboard'}
                  </Link>

                  {isAccountManagementEnabled ? (
                    <>
                      <Link
                        href={Route.ORDER_HISTORY}
                        data-cy="header-order-history"
                      >
                        Order History
                      </Link>
                      <Link
                        href={Route.TRACKING}
                        data-cy="header-track-package"
                      >
                        Track Package
                      </Link>
                    </>
                  ) : (
                    <li>
                      <span className="c-nav-header__link">
                        Menus
                        <CaretIcon
                          className="c-svg-fill-primary-color"
                          width="12"
                          focusable="false"
                        />
                      </span>

                      <ul className="c-nav-header__sublist">
                        <SubLink
                          href={Route.WEEKLY_MENU_MEALS}
                          data-cy="header-meal-kits"
                          reload
                        >
                          Meal Kits
                        </SubLink>

                        <SubLink
                          href={Route.WEEKLY_MENU_READY_TO_EAT}
                          data-cy="header-prepared-meals"
                          reload
                        >
                          Ready To Eat
                        </SubLink>

                        <SubLink
                          href={Route.GROCERY_MEALS}
                          data-cy="header-plantry-meals"
                          reload
                        >
                          Grocery
                        </SubLink>

                        {isBundlesEnabled && isBundlesAvailable && (
                        <div className="d-none speero_bundles">
                          <SubLink
                            href={Route.BUNDLES}
                            data-cy="header-bundles"
                            reload
                          >
                            <abbr>
                              Bundles
                            </abbr>
                          </SubLink>
                        </div>
                        )}
                        {hasFrozenMealsEnabled && (
                        <SubLink
                          href={Route.FROZEN_MEALS}
                          data-cy="header-frozen-meals"
                          reload
                        >
                          Frozen Meals
                        </SubLink>
                        )}
                      </ul>
                    </li>
                  )}

                  <Link href={Route.GIFTS} data-cy="header-gifts" reload>
                    Gifts
                  </Link>
                </>
              )}

              <UserBadge />
            </ul>
          </nav>
        )}

        <button
          type="button"
          className="d-mapp-block c-header__close-btn"
          aria-label="Close"
          onClick={() => closeWebview()}
        >
          <CloseIcon
            className="c-svg-fill-primary-color"
            width="32"
            height="32"
          />
        </button>
      </div>
    </header>
  );
}

export default Header;
